import { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Mail } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email,
        source: "landing_page",
      });
      if (res.data?.already_subscribed) {
        toast.success("You're already on the list — thank you!");
      } else {
        toast.success("Welcome to the Kendi Collections community.");
        if (typeof window !== "undefined" && window.trackNewsletterSignup) {
          window.trackNewsletterSignup("landing_page");
        }
      }
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Subscription failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-20 bg-[#0B0B0B] border-t border-b border-[#1a1a1a]"
    >
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#0B0B0B",
            border: "1px solid #D4AF37",
            color: "#F5F5F5",
            borderRadius: 0,
            fontFamily: "Montserrat, sans-serif",
            fontSize: "13px",
            letterSpacing: "0.04em",
          },
        }}
      />

      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: title */}
          <div className="lg:col-span-5 reveal">
            <span className="text-[11px] tracking-[0.42em] uppercase text-[#D4AF37]">
              The List
            </span>
            <h2 className="mt-5 font-serif-display text-[34px] sm:text-4xl md:text-5xl leading-[1.05] tracking-tight text-white">
              Join the Kendi Collections{" "}
              <span className="italic text-gold-shimmer">community.</span>
            </h2>
            <p className="mt-5 text-[14px] leading-[1.85] text-white/60 font-light max-w-md">
              Quiet emails. New arrivals from the Mara, Marrakech, and Melbourne.
              No spam. Unsubscribe any time.
            </p>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="newsletter-form"
              className="flex flex-col sm:flex-row items-stretch border border-[#262626] hover:border-[#D4AF37]/40 transition-colors duration-500 bg-[#0e0e0e]"
            >
              <div className="flex items-center gap-3 px-5 sm:px-6 flex-1 py-1">
                <Mail className="w-4 h-4 text-[#D4AF37] shrink-0" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  data-testid="newsletter-email-input"
                  className="w-full bg-transparent text-white placeholder-white/30 outline-none border-none py-4 text-[14px] tracking-wide font-light"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="bg-[#D4AF37] hover:bg-[#B08D27] disabled:opacity-60 disabled:cursor-not-allowed text-black font-semibold uppercase tracking-[0.18em] text-[11px] px-7 py-4 sm:py-0 transition-colors duration-300 flex items-center justify-center gap-2 min-h-[52px] sm:min-w-[180px]"
              >
                {loading ? (
                  "Joining…"
                ) : (
                  <>
                    Subscribe
                    <ArrowUpRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
            <p className="mt-4 text-[10px] tracking-[0.28em] uppercase text-white/35">
              By subscribing you agree to our quiet, occasional updates.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
